<p-toast role="status" aria-live="polite" aria-atomic="true"></p-toast>

<main
  class="main-run-wrapper surface-ground min-h-screen p-3 md:p-6 flex justify-content-center"
  role="main"
  aria-labelledby="questionnaire-run-title"
>
  @if (isLoading()) {
    <div
      class="flex flex-column align-items-center justify-content-center w-full"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <p-progress-spinner strokeWidth="3" aria-hidden="true"></p-progress-spinner>
      <p class="text-xl text-500 mt-3 font-medium">Caricamento questionario...</p>
    </div>
  }

  @if (!isLoading() && questionnaire(); as data) {
    <div
      class="card shadow-2 w-full max-w-6xl surface-card border-round-xl p-4 md:p-6 animate-fadein"
    >
      <header class="text-center mb-6">
        <h1 id="questionnaire-run-title" class="text-4xl font-bold m-0 text-primary">
          {{ data.name }}
        </h1>
        @if (data.tooltip) {
          <p class="text-secondary mt-2 mb-0 text-lg font-normal">
            {{ data.tooltip }}
          </p>
        }
        <p class="text-secondary mt-2 text-lg font-medium">
          Completa le informazioni richieste per procedere
        </p>
      </header>

      <form [formGroup]="form" class="p-fluid">
        <div class="flex flex-column gap-6">
          @for (question of data.questions; track question.id || $index; let i = $index) {
            <section
              class="col-12 p-0"
              [attr.aria-labelledby]="'question-title-' + question.id"
              [attr.aria-describedby]="
                getControl(question.id).invalid && getControl(question.id).touched
                  ? 'question-error-' + question.id
                  : null
              "
            >
              <!-- HEADER DOMANDA -->
              <div class="flex align-items-center justify-content-between mb-3">
                <div>
                  <h2
                    [attr.id]="'question-title-' + question.id"
                    class="block font-bold text-900 text-2xl m-0"
                  >
                    <span class="text-primary mr-2">{{ i + 1 }}.</span>
                    {{ question.text }}
                  </h2>
                  @if (question.tooltip) {
                    <p class="text-secondary text-sm font-normal mt-2 mb-0 line-height-3">
                      {{ question.tooltip }}
                    </p>
                  }
                </div>

                <div class="flex gap-2">
                  @if (question.mandatory && question.type !== 'MULTISELECT') {
                    <p-tag value="Obbligatorio" severity="warn" [rounded]="true"></p-tag>
                  } @else {
                    <p-tag value="Opzionale" severity="success" [rounded]="true"></p-tag>
                  }
                </div>
              </div>

              <!-- BOX RISPOSTA -->
              <div
                class="question-run-item p-4 border-round-xl border-1 transition-all"
                [ngClass]="{
                  'border-red-400 bg-red-50':
                    getControl(question.id).invalid && getControl(question.id).touched,
                  'border-200 surface-50':
                    !getControl(question.id).invalid || !getControl(question.id).touched,
                }"
              >
                <!-- SINGLESELECT -->
                @if (isQuestionType(question, 'SINGLESELECT')) {
                  <fieldset class="radio-fieldset">
                    <legend class="visually-hidden" [id]="'question-legend-' + question.id">
                      {{ question.text }}
                    </legend>
                    <div
                      class="flex flex-column gap-3"
                      role="radiogroup"
                      [attr.aria-labelledby]="'question-title-' + question.id"
                      [attr.aria-required]="question.mandatory ? 'true' : null"
                      [attr.aria-invalid]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'true'
                          : 'false'
                      "
                      [attr.aria-describedby]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'question-error-' + question.id
                          : null
                      "
                    >
                    @for (option of question.options; track option.id) {
                      <!-- Usa l'ID dell'opzione come valore, ma il controllo è della domanda -->
                      <div
                        class="flex align-items-center bg-white p-3 shadow-sm border-round-lg border-1 border-100 hover:border-primary transition-colors cursor-pointer"
                        (click)="form.get(question.id)?.setValue(option.id)"
                      >
                        <p-radio-button
                          [name]="question.id"
                          [inputId]="question.id + option.id"
                          [value]="option.id"
                          [formControlName]="question.id"
                          (onClick)="$event.originalEvent.stopPropagation()"
                          [ariaLabel]="option.text"
                        >
                        </p-radio-button>

                        <label
                          [for]="question.id + option.id"
                          class="w-full text-xl font-medium cursor-pointer ml-3"
                          (click)="$event.preventDefault()"
                        >
                          {{ option.text }}
                        </label>
                      </div>
                    }
                    </div>
                  </fieldset>
                }

                <!-- MULTISELECT -->
                @if (isQuestionType(question, 'MULTISELECT')) {
                  <fieldset class="checkbox-fieldset">
                    <legend class="visually-hidden" [id]="'question-legend-' + question.id">
                      {{ question.text }}
                    </legend>
                    <div
                      class="flex flex-column gap-3"
                      role="group"
                      [attr.aria-labelledby]="'question-title-' + question.id"
                      [attr.aria-required]="question.mandatory ? 'true' : null"
                      [attr.aria-invalid]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'true'
                          : 'false'
                      "
                      [attr.aria-describedby]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'question-error-' + question.id
                          : null
                      "
                    >
                    @for (option of question.options; track option.id) {
                      <div
                        class="flex align-items-center bg-white p-3 shadow-sm border-round-lg border-1 border-100 transition-all hover:border-primary"
                      >
                        <p-checkbox
                          [inputId]="'check-' + question.id + option.id"
                          [value]="option.id"
                          [binary]="false"
                          [ngModel]="isOptionChecked(question.id, option.id)"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onMultiChoiceChange(question.id, option.id, $event)"
                          class="mr-3"
                          [ariaLabel]="option.text"
                        >
                        </p-checkbox>

                        <label
                          [for]="'check-' + question.id + option.id"
                          class="w-full text-xl font-medium cursor-pointer"
                        >
                          {{ option.text }}
                        </label>
                      </div>
                    }
                    </div>
                  </fieldset>
                }

                <!-- TEXT -->
                @if (isQuestionType(question, 'TEXT')) {
                  <p class="text-sm text-color-secondary mb-2" [id]="'text-help-'+question.id">
                    <i class="pi pi-info-circle mr-1"></i>
                    Non inserire tag HTML o codice (es. &lt;script&gt;).
                  </p>
                  <input
                    pInputText
                    [formControlName]="question.id"
                    class="w-full"
                    placeholder="Inserisci risposta..."
                    [attr.aria-label]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="'text-help-'+question.id + ' ' + (
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : ''
                    )"
                  />
                }

                <!-- NUMBER -->
                @if (isQuestionType(question, 'NUMBER')) {
                  <p-input-number
                    [formControlName]="question.id"
                    styleClass="w-full"
                    placeholder="Inserisci valore numerico..."
                    [ariaLabel]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : null
                    "
                  ></p-input-number>
                }

                <!-- DATE -->
                @if (isQuestionType(question, 'DATE')) {
                  <p class="text-sm text-color-secondary mb-2" [id]="'date-help-'+question.id">
                    <i class="pi pi-info-circle mr-1"></i>
                    Formato: YYYY-MM-DD (esempio: 2026-04-21)
                  </p>
                  <p-date-picker
                    [formControlName]="question.id"
                    styleClass="w-full"
                    placeholder="YYYY-MM-DD"
                    [ariaLabel]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="'date-help-'+question.id + ' ' + (
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : ''
                    )"
                  ></p-date-picker>
                }

                <!-- BOOLEAN -->
                @if (isQuestionType(question, 'BOOLEAN')) {
                  <div
                    class="flex align-items-center gap-3 p-2"
                    role="group"
                    [attr.aria-labelledby]="'question-title-' + question.id"
                    [attr.aria-describedby]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : null
                    "
                  >
                    <p-checkbox
                      [formControlName]="question.id"
                      [binary]="true"
                      [inputId]="question.id"
                      [ariaLabel]="question.options[0]?.text || question.text"
                    ></p-checkbox>
                    <label [for]="question.id" class="text-xl cursor-pointer font-medium">{{
                      question.options[0]?.text
                    }}</label>
                  </div>
                }

                <!-- CAMPO ALTRO DINAMICO -->
                @if (isOtherSelected(question.id)) {
                  <div
                    class="mt-4 p-3 border-round-lg border-left-3 border-primary bg-white shadow-1 animate-fadein"
                  >
                    <label
                      [for]="question.id + '_otherText'"
                      class="block font-semibold mb-2 text-primary"
                      >Specifica il dettaglio per 'Altro':</label
                    >
                    <input
                      pInputText
                      [formControlName]="question.id + '_otherText'"
                      [id]="question.id + '_otherText'"
                      class="w-full"
                      placeholder="Dettagli..."
                      [attr.aria-required]="'true'"
                      [attr.aria-invalid]="
                        getControl(question.id + '_otherText').invalid &&
                        getControl(question.id + '_otherText').touched
                          ? 'true'
                          : 'false'
                      "
                      [attr.aria-describedby]="
                        getControl(question.id + '_otherText').invalid &&
                        getControl(question.id + '_otherText').touched
                          ? 'question-other-error-' + question.id
                          : null
                      "
                    />
                    @if (
                      getControl(question.id + '_otherText').invalid &&
                      getControl(question.id + '_otherText').touched
                    ) {
                      <small
                        [attr.id]="'question-other-error-' + question.id"
                        class="p-error block mt-2 font-semibold"
                        role="alert"
                        aria-live="assertive"
                      >
                        <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i>
                        Specifica il dettaglio richiesto
                      </small>
                    }
                  </div>
                }
              </div>

              @if (getControl(question.id).invalid && getControl(question.id).touched) {
                <small
                  [attr.id]="'question-error-' + question.id"
                  class="p-error block mt-2 ml-2 font-semibold"
                  role="alert"
                  aria-live="assertive"
                >
                  <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i> Risposta
                  necessaria
                </small>
              }
            </section>
          }
        </div>

        <!-- AZIONI FINALI -->
        <div class="flex justify-content-end gap-3 mt-8 pt-5 border-top-1 border-100">
          <p-button
            label="Chiudi Questionario"
            icon="pi pi-times-circle"
            severity="secondary"
            [disabled]="isSubmitting()"
            (click)="closeQuestionnaire()"
            styleClass="shadow-2"
            [raised]="true"
            ariaLabel="Chiudi questionario e torna alla schermata precedente"
            title="Chiudi questionario"
          >
          </p-button>
        </div>
      </form>
    </div>
  }
</main>
